import { ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useIsDesktop } from 'Hooks';

interface ISimpleToggle {
  option1: string;
  option2: string;
  setState: (isOption1Select: boolean) => void;
  isOption2?: boolean;
  alignment?: string;
  width?: string;
  selectedBgcolor?: string;
  variant?: 'default' | 'pill';
}

export const SimpleToggle = (props: ISimpleToggle) => {
  const isDesktop = useIsDesktop();
  const isPill = props.variant === 'pill';
  const selectedBgcolor =
    props.selectedBgcolor ?? (isPill ? 'primary.main' : 'purple2.main');

  const toggleChangeHandler = (
    event: React.MouseEvent<HTMLElement>,
    toggleValue: string,
  ) => {
    if (toggleValue === null) return;
    props.setState(toggleValue === props.option1);
  };

  return (
    <ToggleButtonGroup
      value={props.alignment}
      exclusive
      onChange={toggleChangeHandler}
      sx={
        isPill
          ? {
              bgcolor: '#fff',
              border: 'none',
              borderRadius: '24px',
              p: '4px',
              width: props.width ?? '100%',
              boxShadow: isDesktop
                ? '0px 0px 6px rgba(45, 112, 217, 0.16)'
                : '0px -1px 5px rgba(0, 0, 0, 0.07)',
              '& .MuiToggleButtonGroup-grouped': {
                border: 'none',
                margin: 0,
                '&:not(:first-of-type)': {
                  borderLeft: 'none',
                  marginLeft: 0,
                },
              },
              '&&& .Mui-selected': {
                bgcolor: selectedBgcolor,
                color: 'white',
                fontWeight: 700,
                borderRadius: '24px',
                '&:hover': {
                  bgcolor: selectedBgcolor,
                },
              },
              direction: 'rtl',
            }
          : {
              bgcolor: '#fff',
              border: '1px solid',
              borderColor: 'primary.shade_200',
              borderRadius: '6px',
              width: props.width ? props.width : '100%',
              minWidth: isDesktop ? '400px' : undefined,
              label: {
                fontSize: '18px',
                fontWeight: '400',
                cursor: 'pointer',
              },
              '&&& .Mui-selected': {
                bgcolor: selectedBgcolor,
                color: 'white',
                fontWeight: 700,
              },
              direction: 'rtl',
            }
      }
      role='tabpanel'
    >
      <ToggleButton
        value={props.option1}
        sx={
          isPill
            ? {
                width: '100%',
                border: 'none',
                borderRadius: '24px !important',
                color: 'text.primary',
                py: '8px',
                px: '18px',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'transparent',
                },
                '&.Mui-selected:hover': {
                  bgcolor: selectedBgcolor,
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '2px',
                },
              }
            : {
                width: '100%',
                borderRadius: '0 6px 6px 0 !important',
                border: 'none',
                '&:focus': {
                  bgcolor: '#081E54 !important',
                  color: 'white',
                },
              }
        }
        role='tab'
        aria-selected={props.alignment === props.option1}
        aria-pressed={undefined}
      >
        <Typography
          variant={'body3'}
          sx={
            isPill
              ? {
                  fontSize: '18px',
                  lineHeight: '24px',
                  fontWeight: props.alignment === props.option1 ? 700 : 400,
                }
              : undefined
          }
        >
          {props.option1}
        </Typography>
      </ToggleButton>
      <ToggleButton
        value={props.option2}
        sx={
          isPill
            ? {
                width: '100%',
                border: 'none !important',
                borderRadius: '24px !important',
                color: 'text.primary',
                py: '8px',
                px: '18px',
                textTransform: 'none',
                ml: '0px !important',
                '&:hover': {
                  bgcolor: 'transparent',
                },
                '&.Mui-selected:hover': {
                  bgcolor: selectedBgcolor,
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '2px',
                },
              }
            : {
                width: '100%',
                borderRadius: '6px 0 0 6px !important',
                border: 'none !important',
                borderRight: '1px solid !important',
                '&:focus': {
                  bgcolor: '#081E54 !important',
                  color: 'white',
                },
                ml: '0px !important',
                mr: '-1px',
              }
        }
        role='tab'
        aria-selected={props.alignment === props.option2}
        aria-pressed={undefined}
      >
        <Typography
          variant={'body3'}
          sx={
            isPill
              ? {
                  fontSize: '18px',
                  lineHeight: '24px',
                  fontWeight: props.alignment === props.option2 ? 700 : 400,
                }
              : undefined
          }
        >
          {props.option2}
        </Typography>
      </ToggleButton>
    </ToggleButtonGroup>
  );
};
